// CRM-LEADS-2026-10-16 §7: gather -- the selected leads on the filtered list as a paste-able table, a CSV, or one text sent
// to each (at most 25 at a time), one at a time through the agents window's /api/staff-text, which keeps its STOP and
// quiet-hour rules per person. Each send lands on that lead's own conversation (the route logs it as role 'human').
// NIGHT-DIRECTSTAFF-2026-10-17 §1: merge fields {first_name} {discipline} {state} (and the old {first}), a preview, and
// the leads missing a field the message uses.
import { disciplineCode, sourceKind, SOURCES, localDate } from './leadFilters';
import { firstName } from './staffText';

export const BATCH_MAX = 25;
// The staff-text route allows 10 staff texts per rolling 60 s across ALL staff (agents window, 10-16): back to back, a
// batch of 25 stopped at the 11th. One every 6.5 s keeps under it, so the whole batch goes (25 take about 2.7 min).
export const PACE_MS = 6500;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
export const COLUMNS = ['Name', 'Discipline', 'State', 'Source', 'Date'];

const s = (v) => (v === null || v === undefined ? '' : String(v).trim());
const nameOf = (l) => {
  const r = l && l.raw_payload && typeof l.raw_payload === 'object' ? l.raw_payload : {};
  return s(l && l.name) || [s(r.firstName || r.first_name), s(r.lastName || r.last_name)].filter(Boolean).join(' ');
};

/** The merge fields Text all offers, in the order the buttons show them. {first} is the old spelling of {first_name}. */
export const MERGE_FIELDS = {
  first_name: { label: 'first name', value: (l) => firstName({ name: nameOf(l) }) },
  discipline: { label: 'discipline', value: (l) => disciplineCode(l) || s(l && l.discipline) },
  state: { label: 'state', value: (l) => (/^[A-Za-z]{2}$/.test(s(l && l.state)) ? s(l.state).toUpperCase() : s(l && l.state)) },
};
const ALIAS = { first: 'first_name' };
const TOKEN = /\{([A-Za-z_]+)\}/g;
const fieldOf = (t) => ALIAS[t] || t;

/** The merge fields the message uses (canonical names, once each, in MERGE_FIELDS order). */
export function fieldsUsed(body) {
  const seen = new Set();
  for (const m of String(body || '').matchAll(TOKEN)) if (MERGE_FIELDS[fieldOf(m[1])]) seen.add(fieldOf(m[1]));
  return Object.keys(MERGE_FIELDS).filter((k) => seen.has(k));
}
/** {tokens} in the message that aren't merge fields -- sending "{name}" to 25 people is a mistake, so Send waits. */
export function unknownFields(body) {
  const out = [];
  for (const m of String(body || '').matchAll(TOKEN)) if (!MERGE_FIELDS[fieldOf(m[1])] && !out.includes(m[0])) out.push(m[0]);
  return out;
}

/** The message for one lead. A blank field goes, with the space before it ("Hi {first_name}," -> "Hi,"). */
export function mergeFor(body, lead) {
  return String(body || '')
    .replace(/(\s*)\{([A-Za-z_]+)\}/g, (all, sp, t) => {
      const f = MERGE_FIELDS[fieldOf(t)];
      if (!f) return all;
      const v = f.value(lead);
      return v ? `${sp}${v}` : '';
    })
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

/** The leads missing a field the message uses: [{ id, name, missing: ['state', ...] }]. */
export function missingFields(body, leads) {
  const used = fieldsUsed(body);
  if (!used.length) return [];
  return (leads || []).map((l) => ({ id: l.id, name: nameOf(l), missing: used.filter((k) => !MERGE_FIELDS[k].value(l)) }))
    .filter((r) => r.missing.length);
}

/** One row per lead: name, discipline, state, source, date -- nothing else (no phone, no email). */
export function gatherRows(leads) {
  return (leads || []).map((l) => [nameOf(l), disciplineCode(l) || s(l.discipline), s(l.state).toUpperCase(),
    `${SOURCES[sourceKind(l)]}${l.source_site && sourceKind(l) === 'site' ? ` (${s(l.source_site)})` : ''}`, localDate(l.created_at)]);
}

/** Tab-separated, header first: pastes straight into a sheet or an email as a table. */
export function toTSV(leads) {
  const clean = (v) => String(v).replace(/[\t\r\n]+/g, ' ');
  return [COLUMNS, ...gatherRows(leads)].map((r) => r.map(clean).join('\t')).join('\n');
}

// A cell a spreadsheet would run as a formula (=, +, -, @, tab, CR) is prefixed with ' -- CSV injection.
const csvCell = (v) => {
  let t = String(v);
  if (/^[=+\-@\t\r]/.test(t)) t = `'${t}`;
  return /[",\n\r]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
};
export function toCSV(leads) {
  return [COLUMNS, ...gatherRows(leads)].map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n';
}
export const csvFileName = (now = new Date()) => `leads-${localDate(now.toISOString())}.csv`;

/**
 * Text each recipient, ONE AT A TIME, in order: send(leadId, body, sendAnyway) -> the route's answer, or a throw with
 * .code (opted_out, quiet_hours, invalid_number, rate_limited, ...). A refusal is that person's result; the batch goes
 * on. shouldStop() between sends stops it. onEach(result) after every one. pauseMs between sends (not before the first)
 * keeps under the route's limit. -> results [{ id, ok, duplicate, code, message }]
 */
export async function sendBatch({ recipients, body, sendAnyway = false, send, onEach = () => {}, shouldStop = () => false,
  pauseMs = PACE_MS, sleep = wait }) {
  const out = [];
  for (const l of (recipients || []).slice(0, BATCH_MAX)) {
    if (shouldStop()) break;
    if (out.length && pauseMs > 0) { await sleep(pauseMs); if (shouldStop()) break; }
    const text = mergeFor(body, l);
    let r;
    try {
      const res = await send(l.id, text, sendAnyway === true);
      r = { id: l.id, ok: true, duplicate: !!(res && res.duplicate) };
    } catch (e) {
      r = { id: l.id, ok: false, code: (e && e.code) || 'unknown', message: (e && e.message) || 'Not sent.' };
      if (r.code === 'rate_limited') { out.push(r); onEach(r); break; }   // the route's own brake: stop, don't hammer it
    }
    out.push(r);
    onEach(r);
  }
  return out;
}

/** "Sent to 18 · 2 replied STOP · 3 outside quiet hours · 2 other"; total = how many the batch meant to text. */
export function batchSummary(results, total = results.length) {
  const n = (f) => results.filter(f).length;
  const parts = [`Sent to ${n((r) => r.ok)}`];
  const stop = n((r) => !r.ok && r.code === 'opted_out');
  const quiet = n((r) => !r.ok && r.code === 'quiet_hours');
  const other = n((r) => !r.ok && r.code !== 'opted_out' && r.code !== 'quiet_hours');
  if (stop) parts.push(`${stop} replied STOP`);
  if (quiet) parts.push(`${quiet} outside their 8am-8pm`);
  if (other) parts.push(`${other} not sent`);
  const left = total - results.length;
  if (left > 0) parts.push(results.length && results[results.length - 1].code === 'rate_limited'
    ? `stopped at the texting limit, ${left} not tried: wait a minute, then text the rest` : `stopped, ${left} not tried`);
  return parts.join(' · ');
}
